import { Injectable } from '@angular/core';
import { Themes } from '@metaos/hub-sdk';
import { OfficeStartTheme } from '@office-start/theming-service';
import { BaseTheme, FullBleedImagesInfo, ShellThemeInfo, ThemeType } from '@suiteux/suiteux-shell';
import { combineLatest, Observable, of as observableOf } from 'rxjs';
import { distinctUntilChanged, filter, map, merge, shareReplay, skip, startWith } from 'rxjs/operators';

import { FabricConstants } from '../constants/fabric.constant';
import { OfficeRoute } from '../constants/office-route.enum';
import { GrayscaleThemeShade, ThemeRootCssClass, ThemeShade } from '../constants/theme-shade.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FileItem, FileType } from '../models/file-item.model';
import { ObservableScope } from '../models/observable-scope.enum';
import { AngularEvents } from '../shared-context/angular-events';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { LocService } from '../shared-context/loc.service';
import { UserContextService } from '../shared-context/user-context.service';
import { ScopableObservable, ScopableSubject } from '../utilities/scopable-subject.utility';
import { ConfigParserService } from './config-parser.service';
import { DocumentService } from './document.service';
import { EventService } from './event.service';
import { OfficeRouteService } from './office-route.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { ShellShimService } from './shell-shim.service';
import { WebPwaService } from './web-pwa.service';
import { WindowService } from './window.service';

interface ThemeConfig {
    appPageTypeOnLoad: string;
    preferOfficeTheme: boolean;
    useTenantTheme: boolean;
    userThemeShades: Array<string>;
    wordThemeShades: Array<string>;
    wordThemeShadesDark: Array<string>;
    excelThemeShades: Array<string>;
    excelThemeShadesDark: Array<string>;
    powerpointThemeShades: Array<string>;
    powerpointThemeShadesDark: Array<string>;
    swayThemeShades: Array<string>;
    swayThemeShadesDark: Array<string>;
    homePageTitle: string;
    onenoteThemeShades: Array<string>;
    onenoteThemeShadesDark: Array<string>;
    formsThemeShades: Array<string>;
    formsThemeShadesDark: Array<string>;
    visioThemeShades: Array<string>;
    visioThemeShadesDark: Array<string>;
    isDarkMode: boolean;
    streamThemeShades: Array<string>;
    streamThemeShadesDark: Array<string>;
}

interface AppThemeConfig {
    theme: BaseTheme;
    darkTheme?: BaseTheme;
    headerText: string;
    helpPaneNamespace?: string;
}

export interface StartingTheme extends ThemeChangeEvent {
    appPage?: FileType;
}

// TODO: simplify this interface once the React Shell is the default. Only the reactShellTheme property
// should be needed then.
export interface Theme {
    shades: Array<string>; // Shade colors of form '#abcdef'
    reactShellTheme: ShellThemeInfo; // This is only set for themes in the React Shell.
    themeType: ThemeType; // Default, User, Tenant, App
    themeId: string;
    grayscaleThemeShades?: Array<string>;
}

export interface ThemeChangeEvent {
    theme: Theme;
    userSelected: boolean; // Is false if the theme changed due to going to an app start page
}

export interface FullBleedThemeChangeEvent {
    shouldFullBleed: boolean;
    bleedColor: string;
    bleedFontColor: string;
    themeData: ThemeChangeEvent;
}

export enum FullBleedImageType {
    Side = 'Side',
    Pattern = 'Pattern',
    Default = 'Default'
}

@Injectable()
export abstract class ThemingService {

    private static readonly staticDarkTheme = 'dark-mode';

    // TODO: Is this still needed or can we rely on the dark theme from shell in PWA?
    protected static readonly darkTheme = {
        IsDarkMode: true,
        ThemeType: 'User',
        ThemeId: ThemingService.staticDarkTheme,
        NavBar: '#1B1A19',
        Accent: '#F7894A',
        Dark: '#F7894A',
        Darker: '#F7894A',
        DarkAlt: '#F7894A',
        Primary: '#F7894A',
        Secondary: '#F7894A',
        Tertiary: '#F7894A',
        Light: '#F7894A',
        Lighter: '#F7894A',
        LighterAlt: '#F7894A'
    };

    private static readonly msaFluentThemeLight: BaseTheme = {
        Darker: '#004578',
        Dark: '#005A9E',
        DarkAlt: '#106EBE',
        Primary: '#0078D4',
        Secondary: '#2B88D8',
        Tertiary: '#C7E0F4',
        Light: '#DEECF9',
        Lighter: '#EFF6FC',
        LighterAlt: '#EFF6FC'
    };

    private static readonly msaFluentThemeDark: BaseTheme = {
        Darker: '#6CB8F6',
        Dark: '#3AA0F3',
        DarkAlt: '#1890F1',
        Primary: '#0086F0',
        Secondary: '#0074D3',
        Tertiary: '#004F90',
        Light: '#002848',
        Lighter: '#001526',
        LighterAlt: '#001526'
    };

    // TODO: Remove when mobile designs or explore all your apps is updated
    public static readonly eduThemeBackplateOverrides = {
        'backpack': {
            'background-color': 'rgba(255, 179, 221, 0.9)'
        },
        'unicorn': {
            'background-color': 'rgba(72, 117, 46, 0.9)',
            'color': '#FFFFFF'
        }
    };

    private static readonly themeProperties = [{className: 'fcl', propName: 'color'}, {className: 'bgc', propName: 'background-color'}, {className: 'bcl', propName: 'border-color'}, {className: 'bclt', propName: 'border-top-color'}, {className: 'bclb', propName: 'border-bottom-color'}, {className: 'bclr', propName: 'border-right-color'}];
    private static readonly themeSelectorPostfixes = ['f:focus', 'h:hover', 'a:active', 'af:after'];
    // white, neutral lighter alt, neutral lighter, neutral light, neutral quaternary alt (grey 50), neutral quaternary (grey 40), neutral tertiary alt, neutral tertiary, neutral secondary alt, neutral secondary, neutral primary alt, neutral primary, neutral dark, black, white hover, neutral lighter alt hover, neutral light hover, white pressed, neutral lighter alt pressed, neutral light pressed, white selected, neutral light selected, disabled
    protected static readonly grayscaleThemeShades = ['#FFFFFF', '#FAF9F8', '#F3F2F1', '#EDEBE9', '#E1DFDD', '#D2D0CE', '#C8C6C4', '#A19F9D', '#8A8886', '#605E5C', '#3B3A39', '#323130', '#252423', '#000000', '#F3F2F1', '#EDEBE9', '#E1DFDD', '#C8C6C4', '#C8C6C4', '#C8C6C4', '#E1DFDD', '#C8C6C4', '#EDEBE9'];
    protected static readonly grayscaleThemeShadesDark = ['#252423', '#1B1A19', '#F3F2F1', '#323130', '#323130', '#3B3A39', '#484644', '#A19F9D', '#8A8886', '#B3B0AD', '#3B3A39', '#D2D0CE', '#E9E8E8', '#FFFFFF', '#323130', '#323130', '#252423', '#1B1A19', '#1B1A19', '#1B1A19', '#323130', '#252423', '#1B1A19'];
    protected static readonly grayscaleThemeShadesFluent = ['#FFFFFF', '#FAFAFA', '#F5F5F5', '#F0F0F0', '#EBEBEB', '#E5E5E5', '#E0E0E0', '#D1D1D1', '#BDBDBD', '#707070', '#616161', '#424242', '#242424', '#000000', '#F5F5F5', '#F0F0F0', '#EBEBEB', '#E0E0E0', '#DBDBDB', '#D6D6D6', '#EBEBEB', '#E0E0E0', '#F0F0F0'];
    protected static readonly grayscaleThemeShadesFluentDark = ['#292929', '#1F1F1F', '#141414', '#3D3D3D', '#333333', '#2E2E2E', '#525252', '#525252', '#5C5C5C', '#999999', '#ADADAD', '#D6D6D6', '#FFFFFF', '#FFFFFF', '#3D3D3D', '#333333', '#292929', '#1F1F1F', '#141414', '#0A0A0A', '#383838', '#242424', '#141414'];
    protected static readonly baseThemeId = 'base';

    private isPWA: boolean;
    private themeShadeClassPostfixes: Map<ThemeShade, string>;
    private grayscaleThemeShadeClassPostfixes: Map<GrayscaleThemeShade, string>;
    protected supportedAppThemes: Map<FileType, AppThemeConfig>;
    protected themeConfig: ThemeConfig;
    private processedInitialPageLoad: boolean;
    public userTheme: Theme; // represents a theme selected by the user (not a theme that is automatically set on start page navigations)
    private currentRouteApp: FileType;
    private displayedAppTheme: FileType;
    private currentPage: OfficeRoute;
    private _fullBleedTheming: Observable<FullBleedThemeChangeEvent>;
    protected defaultTheme: ShellThemeInfo;

    public themeShades: Observable<ThemeChangeEvent>;
    public darkModeSubscription: Observable<boolean>;

    private readonly fluentThemeSubject: ScopableSubject<OfficeStartTheme> = new ScopableSubject();
    public fluentThemeSubscription: ScopableObservable<OfficeStartTheme> = this.fluentThemeSubject.asScopableObservable();

    private isSearchDarkModeEcsConfigEnable: boolean = false;

    private lastFluentTheme: string;

    public get fullBleedTheming(): Observable<FullBleedThemeChangeEvent> {
        return this._fullBleedTheming;
    }

    constructor(
        private readonly documentService: DocumentService,
        protected readonly eventService: EventService,
        protected readonly capabilitiesService: CapabilitiesService,
        private readonly configParserService: ConfigParserService,
        private readonly clientStartupService: ClientStartupConfigService,
        private readonly contextService: ContextService,
        private readonly instrumentationService: InstrumentationService,
        protected readonly userContext: UserContextService,
        protected readonly routeService: OfficeRouteService,
        private readonly schedulerService: SchedulerService,
        private readonly locService: LocService,
        private readonly windowService: WindowService,
        private readonly shellShimService: ShellShimService,
        private readonly webPwaService: WebPwaService
    ) {
        // Class that extends this needs to call init()
    }

    public getEduHeaderImageForWidth(fullBleedImages: FullBleedImagesInfo, width: number): string {
        if (width >= FabricConstants.ExtraLargeScreenMaxWidth) {
            return fullBleedImages[1920];
        } else if (width >= FabricConstants.LargeScreenMaxWidth) {
            return fullBleedImages[1366];
        } else if (width >= FabricConstants.MediumScreenMaxWidth) {
            return fullBleedImages[1024];
        }
        return fullBleedImages[640];
    }

    protected init() {
        const capabilities = this.capabilitiesService.capabilities;
        this.defaultTheme = (capabilities.fluentHeader ? this.windowService.nativeWindow.commercialDefaultThemeFluent : this.windowService.nativeWindow.commercialDefaultTheme) as ShellThemeInfo;
        if (capabilities.fluentRefresh) {
            this.schedulerService.scheduleWork(() => {
                import(/*webpackChunkName: "initialize-fluent-theme" */ '../office-start/initialize-fluent-theme').then(mod => {
                    mod.initializeFluentTheme(this.fluentThemeSubscription);
                });
            }, SchedulePriority.Immediate);
        }

        this.isPWA = capabilities.pwaEnabled;
        this.themeShadeClassPostfixes = new Map<ThemeShade, string>();
        this.grayscaleThemeShadeClassPostfixes = new Map<GrayscaleThemeShade, string>();
        this.supportedAppThemes = new Map<FileType, AppThemeConfig>();
        this.currentRouteApp = FileType.Unknown;

        const themeChange = this.createThemeChangeObservable();
        this.themeConfig = this.configParserService.parse<ThemeConfig>('themeConfig');
        this.initializeUserTheme();

        const startingTheme = this.getStartingTheme();
        this.themeShades = observableOf(startingTheme).pipe(merge(themeChange), shareReplay(1));
        this.darkModeSubscription = combineLatest(this.themeShades, this.routeService.getRouteChangeEndObservable())
            .pipe(map(([themeShades, routeEvent]) => {
                if (!(routeEvent.currentRoute.route === OfficeRoute.OfficeHome
                    || routeEvent.currentRoute.route === OfficeRoute.Pwa
                    || routeEvent.currentRoute.route === OfficeRoute.AppGallery
                    || routeEvent.currentRoute.route === OfficeRoute.Start
                    || routeEvent.currentRoute.route === OfficeRoute.Templates
                    || routeEvent.currentRoute.route === OfficeRoute.Create
                    || routeEvent.currentRoute.route === OfficeRoute.MyContent
                    || routeEvent.currentRoute.route === OfficeRoute.SpaceGallery
                    || routeEvent.currentRoute.route === OfficeRoute.SpaceContent
                    || routeEvent.currentRoute.route === OfficeRoute.M365Apps
                    || ( this.isSearchDarkModeEcsConfigEnable &&
                        (routeEvent.currentRoute.route === OfficeRoute.SearchPersonScoped
                        || routeEvent.currentRoute.route === OfficeRoute.Search))
                    )) {
                    return false;
                } else {
                    return themeShades.theme.reactShellTheme.IsDarkMode;
                }
            }));

        const darkModeSearchSetEvent = this.eventService.whenEventFired(AngularEvents.darkModeAvailable, ObservableScope.OnlyFuture);
        const showDarkModeSearchEvent = darkModeSearchSetEvent.then(event => event.args[0]);

        this.setShowSearchDarkMode(showDarkModeSearchEvent);

        this.processedInitialPageLoad = false;
        this.displayedAppTheme = FileType.Unknown;
        this.initializeThemeUtilities();

        // The starting theme is not user selected if the user directly navigates to a start page.
        // In this case the themeShades event handler doesn't set the page theme, so we set it here.
        if (!startingTheme.userSelected) {
            this.changeAppTheme(startingTheme.theme.shades, startingTheme.appPage);
        }

        if (capabilities.wacStartPageEnabled || capabilities.fluidSupportEnabled) {
            this.initializeAppThemeChangeHandler();

            this.shellShimService.afterShellRendered.then(() => {
                // if current path is start/template page, set shell theme.
                // We check currentRouteApp here in the case that the user navigated to a start page before the shell loaded.
                const appPageType = (this.themeConfig.appPageTypeOnLoad && FileItem.fileTypeNameToFileType(this.themeConfig.appPageTypeOnLoad))
                                    || this.currentRouteApp;
                if (appPageType !== FileType.Unknown) {
                    this.changeShell(appPageType);
                }

                this.processedInitialPageLoad = true;
            });
        }

        const backgroundImageRouteObservable = this.routeService.getRouteChangeEndObservable().pipe(
            map(routeEvent => routeEvent.currentRoute ?
                !this.capabilitiesService.capabilities.leftNavReact
                && (routeEvent.currentRoute.route === OfficeRoute.OfficeHome || routeEvent.currentRoute.route === OfficeRoute.Pwa) : false),
            distinctUntilChanged()
        );

        // We can skip the first emit since we set this value during ShellBootstrap
        backgroundImageRouteObservable.pipe(skip(1)).subscribe(async onBackgroundImagePage => {
            const shell = await this.shellShimService.getShellShim();
            if (shell && shell.Theme && shell.Theme.SetFullBleedEnabled) {
                shell.Theme.SetFullBleedEnabled(onBackgroundImagePage);
            }
        });

        const distinctThemeChange = this.themeShades.pipe(distinctUntilChanged());

        this._fullBleedTheming = combineLatest(
            distinctThemeChange,
            backgroundImageRouteObservable
        ).pipe(map(([themeEvent, onFullBleedPage]) => (
                // Full Bleed Theming depends and should react to changes in the route and theme
                // The theme should only full bleed when the route is on 'OfficeHome'
                {
                    shouldFullBleed: (this.determineFullBleedTheming(themeEvent)) && onFullBleedPage,
                    bleedColor: this.getNavBarColor(themeEvent),
                    bleedFontColor: this.getBleedFontColor(themeEvent),
                    themeData: themeEvent
                }
        ))).pipe(shareReplay()).pipe(startWith({
            shouldFullBleed: false,
            bleedColor: this.getNavBarColor(startingTheme),
            bleedFontColor: this.getBleedFontColor(startingTheme),
            themeData: startingTheme
        }));

        // Update theme color for pwa when a user changes their theme
        combineLatest(
            distinctThemeChange,
            this.webPwaService.isStandalone
        ).pipe(filter(([_themeChangeEvent, isStandalonePwa]) => isStandalonePwa)).subscribe(([themeChangeEvent]) => {
            if (this.webPwaService.isOfficeApp()) {
                this.documentService.updateThemeColor(this.getNavBarColor(themeChangeEvent));
            }
        });
    }

    protected abstract createThemeChangeObservable(): Observable<ThemeChangeEvent>;

    protected abstract getInitialUserTheme(): Theme;

    protected abstract getStartingTheme(): StartingTheme;

    protected abstract showDarkMode(): boolean;

    protected abstract showContrastMode(): boolean;

    protected getAppBrandTheme(shades: Array<string>): BaseTheme {
        return {
            Darker: shades[0],
            Dark: shades[1],
            DarkAlt: shades[2],
            Primary: shades[3],
            Secondary: shades[4],
            Tertiary: shades[5],
            Light: shades[6],
            Lighter: shades[7],
            LighterAlt: shades[8]
        };
    }

    protected getThemeShades(theme: BaseTheme): Array<string> {
        return [theme.Darker, theme.Dark, theme.DarkAlt, theme.Primary, theme.Secondary, theme.Tertiary, theme.Light, theme.Lighter, theme.LighterAlt];
    }

    protected darkModeSupported(): boolean {
        const isContentPage =  this.currentPage === OfficeRoute.Content;
        const isSearchApp = this.currentPage === OfficeRoute.Search || this.currentPage === OfficeRoute.SearchPersonScoped || this.currentPage === OfficeRoute.SearchSiteScoped;
        const isFluid = this.currentRouteApp === FileType.Fluid  || this.currentRouteApp === FileType.FluidActivityBlueprint;

        return (this.isSearchDarkModeEcsConfigEnable && isSearchApp) || (!isSearchApp && (!isContentPage || !isFluid));
    }

    protected getDefaultDarkTheme(themeId?: string) {
        let theme = ThemingService.darkTheme;

        if (this.capabilitiesService.capabilities.fluentHeader) {
            theme = {...theme, ...this.defaultTheme.DarkTheme};
        }

        if (themeId) {
            theme.ThemeId = themeId;
        }

        const shades = this.getThemeShades(theme);
        return { shades, reactShellTheme: theme as ShellThemeInfo, themeType: 'App' as ThemeType, themeId: theme.ThemeId };
    }

    private async updateShellHeader(headerText: string) {
        const reactShellShim = await this.shellShimService.getShellShim();
        reactShellShim && reactShellShim.Header.UpdateHeaderLink(headerText, null);
    }

    private getNavBarColor(themeEvent: ThemeChangeEvent): string {
        const reactShellTheme = themeEvent.theme.reactShellTheme;
        return reactShellTheme ? reactShellTheme.NavBar || reactShellTheme.Primary : themeEvent.theme.shades[ThemeShade.Primary];
    }

    private getBleedFontColor(themeEvent: ThemeChangeEvent): string {
        const reactShellTheme = themeEvent.theme.reactShellTheme;
        return reactShellTheme && reactShellTheme.DefaultText ? reactShellTheme.DefaultText : '#FFFFFF';
    }

    private determineFullBleedTheming(themeEvent: ThemeChangeEvent): boolean {
        const isFullBleedTheme = themeEvent.theme.reactShellTheme && themeEvent.theme.reactShellTheme.FullBleedImages !== undefined && themeEvent.theme.reactShellTheme.FullBleedImages !== null;
        return !this.capabilitiesService.capabilities.leftNavReact && isFullBleedTheme;
    }

    /*
    When modifying theme changing code, consider the following scenarios.
    tenant theme/no tenant theme
        default/custom theme selected
            office.com -> change theme -> nav to start page
            office.com -> nav to start page
            office.com -> nav to start page -> change theme
            office.com -> nav to start page -> nav back
            office.com -> nav to start page -> change theme -> nav back
            direct nav start page
            direct nav start page -> change theme
            direct nav start page -> nav back office.com
    no tenant theme
        default/custom theme selected
            office.com -> nav to start page -> change theme -> change back to default/custom
            office.com -> change theme -> nav to start page -> change theme -> change back to default
    */
    private changeShell(app: FileType) {
        this.changeShellAppTheme(app);
        this.changeShellAppHeader(app);
        this.changeShellHelpPane(app);
    }

    private async changeShellAppTheme(app: FileType, forceCheck = false) {
        if (!this.supportedAppThemes.has(app) || (app === this.displayedAppTheme && !forceCheck)) {
            return;
        }

        const theme = this.supportedAppThemes.get(app);

        const themeToShow = this.showDarkMode() ? theme.darkTheme || theme.theme : theme.theme;
        if (!themeToShow) {
            return;
        }

        const shades = this.getThemeShades(themeToShow);

        if (app !== this.displayedAppTheme) {
            this.changeAppTheme(shades, app);
        }

        const shellShim = await this.shellShimService.getShellShim();

        const shellAppTheme = this.capabilitiesService.capabilities.fluentRefresh ? { ...this.defaultTheme, ...theme.theme, AppName: theme.theme.Primary, Accent: theme.theme.Dark } : theme.theme;

        if (theme.darkTheme) {
            shellAppTheme.DarkTheme = this.capabilitiesService.capabilities.fluentRefresh ? {...this.defaultTheme.DarkTheme, ...theme.darkTheme, AppName: theme.darkTheme.Primary } : theme.darkTheme;
        }

        shellShim.Theme.SetAppBrandTheme(shellAppTheme);

        // The events need to be generated for react shell even though theme did not change.
        // This is needed to keep the functionality for resetting shell changes (including header title)
        const args = [themeToShow, false];
        this.eventService.broadcastEvent({
            name: AngularEvents.themeChanged,
            args
        });

        this.displayedAppTheme = app;
    }

    private changeShellAppHeader(app: FileType) {
        if (!this.supportedAppThemes.has(app)) {
            return;
        }

        const theme = this.supportedAppThemes.get(app);

        this.updateShellHeader(theme.headerText);
    }

    private async changeShellHelpPane(app: FileType) {
        if (!this.supportedAppThemes.has(app)) {
            return;
        }

        const theme = this.supportedAppThemes.get(app);
        if (!theme.helpPaneNamespace) {
            return;
        }

        const reactShellShim = await this.shellShimService.getShellShim();
        reactShellShim.NFD.UpdateHelpNamespace(theme.helpPaneNamespace);
    }

    private async resetShellHelpPane() {
        let namespace = '';
        if (this.isPWA) {
            namespace = this.clientStartupService.config.authType === AuthType.OrgId ? 'IWPWA' : 'MSAPWA';
        } else {
            namespace = this.clientStartupService.config.authType === AuthType.OrgId ? 'IWAPP' : 'IWAPPC';
        }

        const reactShellShim = await this.shellShimService.getShellShim();
        reactShellShim.NFD.UpdateHelpNamespace(namespace);
    }

    private async resetShellTheme() {
        if (this.displayedAppTheme === FileType.Unknown) {
            return;
        }

        if (this.clientStartupService.config.authType === AuthType.MSA) {
            this.changeAppTheme(this.userTheme.shades);
        }

        const shellShim = await this.shellShimService.getShellShim();

        // In commercial, this will trigger a theme change event where office.com app content will be rethemed.
        shellShim.Theme.SetAppBrandTheme(this.defaultTheme);

        // The events need to be generated for react shell even though theme did not change.
        // This is needed to keep the functionality for resetting shell changes (including header title)
        const args = [this.userTheme.reactShellTheme, false];

        this.eventService.broadcastEvent({
            name: AngularEvents.themeChanged,
            args
        });

        this.displayedAppTheme = FileType.Unknown;
    }

    private async resetShellHeader() {
        if (this.isPWA && this.userTheme.themeType !== 'Tenant') {
            this.updateShellHeader('');
        } else {
            this.updateShellHeader(this.themeConfig.homePageTitle);
        }
    }

    private async resetShell() {
        this.resetShellTheme();
        this.resetShellHeader();
        this.resetShellHelpPane();
    }

    private initializeThemeUtilities() {
        this.themeShadeClassPostfixes.set(ThemeShade.Darker, 'tdr');
        this.themeShadeClassPostfixes.set(ThemeShade.Dark, 'td');
        this.themeShadeClassPostfixes.set(ThemeShade.DarkAlt, 'tda');
        this.themeShadeClassPostfixes.set(ThemeShade.Primary, 'tp');
        this.themeShadeClassPostfixes.set(ThemeShade.Secondary, 'ts');
        this.themeShadeClassPostfixes.set(ThemeShade.Tertiary, 'tt');
        this.themeShadeClassPostfixes.set(ThemeShade.Light, 'tl');
        this.themeShadeClassPostfixes.set(ThemeShade.Lighter, 'tlr');
        this.themeShadeClassPostfixes.set(ThemeShade.LighterAlt, 'tlra');

        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.White, 'w');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralLighterAlt, 'nla');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralLighter, 'nlr');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralLight, 'nl');

        // TODO: Replace g40, g50 with quaternary class names when we update existing usages
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralQuaternaryAlt, 'g50');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralQuaternary, 'g40');
        //this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralQuaternaryAlt, 'nqa');
        //this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralQuaternary, 'nq');

        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralTertiaryAlt, 'nta');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralTertiary, 'nt');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralSecondaryAlt, 'nsa');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralSecondary, 'ns');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralPrimaryAlt, 'npa');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralPrimary, 'np');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralDark, 'nd');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.Black, 'bl');

        // Temporary interactive shades for Phase 2 of fluent theme migration
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.WhiteHover, 'wh');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralLighterAltHover, 'nlah');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralLighterHover, 'nlh');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.WhitePressed, 'wp');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralLighterAltPressed, 'nlap');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralLighterPressed, 'nlp');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.WhiteSelected, 'ws');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.NeutralLighterSelected, 'nls');
        this.grayscaleThemeShadeClassPostfixes.set(GrayscaleThemeShade.Disabled, 'd');
        // NeutralLighterAlt Selected is same as neutralQuaternary (g40)

        // Update custom theme css when theme changes
        this.themeShades.subscribe(event => {
            const theme = event.theme;

            // If the theme isn't user selected then skip handling.
            // Note: the React Shell fires a theme change event after calling SetAppBrandTheme, which will come here as a user selected theme.
            if (!event.userSelected) {
                return;
            }

            this.userTheme = theme;

            // If we are already on an app page, we would only need to modify the Shell theming if
            // the user had the default theme selected originally. Otherwise, we assume the app theme has
            // already been applied to the officeHomeRoot elements.
            // This handles case where user has default theme on office.com -> navigates to start page -> changes theme on start page -> changes theme back to default
            // NOTE: The case above (where the app theme has already been applied to the officeHomeRoot elements in the case of being on an app page)
            // is no longer true with the introduction of darkmode to app pages
            if (this.currentRouteApp !== FileType.Unknown && (this.currentRouteApp === FileType.FluidActivityBlueprint || this.currentRouteApp === FileType.Fluid)) {
                this.changeAppTheme(this.userTheme.shades);
                return;
            }

            if (this.currentRouteApp !== FileType.Unknown) {
                const supportedAppTheme = this.supportedAppThemes.get(this.currentRouteApp);
                const appTheme = this.showDarkMode() ? supportedAppTheme?.darkTheme || supportedAppTheme?.theme : supportedAppTheme?.theme;
                const themeShades = appTheme ? this.getThemeShades(appTheme) : this.userTheme.shades;
                const appPage = appTheme ? this.currentRouteApp : null;
                this.changeAppTheme(themeShades, appPage);
                return;
            }

            if (this.isPWA) {
                if (this.userTheme.themeType !== 'Tenant') {
                    this.updateShellHeader('');
                } else {
                    this.updateShellHeader(this.themeConfig.homePageTitle);
                }
            }
            this.changeAppTheme(this.userTheme.shades);
        });
    }

    private async setShowSearchDarkMode(show: Promise<boolean>) {
        this.isSearchDarkModeEcsConfigEnable = await show;
    }

    private initializeAppThemeChangeHandler() {
        this.routeService.getRouteChangeStartObservable().pipe(
            filter(event => (event.currentRoute && event.currentRoute.route !== OfficeRoute.Content) ||
                (event.currentRoute?.route === OfficeRoute.Content && (event.currentRoute.app === FileType.Fluid || event.currentRoute.app === FileType.FluidActivityBlueprint)))
        ).subscribe(event => {
            this.currentRouteApp = event.currentRoute && event.currentRoute.app ? event.currentRoute.app : FileType.Unknown;
            this.currentPage = event.currentRoute.route;

            if (!this.processedInitialPageLoad) {
                return;
            }

            // Fluid expects to use the homepage theme, so we reset theme and header text, but change the help pane.
            // Theme needs to be reset here to account for left-nav scenarios where the user creates a new Fluid doc
            // from a start page.
            if (this.currentRouteApp === FileType.Fluid || this.currentRouteApp === FileType.FluidActivityBlueprint) {
                this.resetShellTheme();
                this.resetShellHeader();
                this.changeShellHelpPane(this.currentRouteApp);
                this.changeAppTheme(this.userTheme.shades);
            } else if (this.currentRouteApp) { // on start page
                this.changeShell(this.currentRouteApp);
            } else {
                this.resetShell();
                if (this.capabilitiesService.capabilities.darkModeEnabled) {
                    this.changeAppTheme(this.userTheme.shades);
                }
            }
        });
    }

    private initializeUserTheme() {
        const config = this.themeConfig;

        // If updating string values here you need to update them in GetDefaultHeaderText() in OfficeHomeModelBuilder.cs as well
        this.supportedAppThemes.set(FileType.Word, { theme: this.configThemeShadesToTheme(config.wordThemeShades), darkTheme: this.configThemeShadesToTheme(config.wordThemeShadesDark), headerText: this.locService.WordName, helpPaneNamespace: 'WDWAENDUSER' });
        this.supportedAppThemes.set(FileType.Excel, { theme: this.configThemeShadesToTheme(config.excelThemeShades), darkTheme: this.configThemeShadesToTheme(config.excelThemeShadesDark), headerText: this.locService.ExcelName, helpPaneNamespace: 'XLWAENDUSER' });
        this.supportedAppThemes.set(FileType.PowerPoint, { theme: this.configThemeShadesToTheme(config.powerpointThemeShades), darkTheme: this.configThemeShadesToTheme(config.powerpointThemeShadesDark), headerText: this.locService.PowerPointName, helpPaneNamespace: 'PPWAENDUSER' });
        this.supportedAppThemes.set(FileType.Sway, { theme: this.configThemeShadesToTheme(config.swayThemeShades), darkTheme: this.configThemeShadesToTheme(config.swayThemeShadesDark), headerText: this.locService.SwayName, helpPaneNamespace: 'STORYIM' });
        this.supportedAppThemes.set(FileType.OneNote, { theme: this.configThemeShadesToTheme(config.onenoteThemeShades), darkTheme: this.configThemeShadesToTheme(config.onenoteThemeShadesDark), headerText: this.locService.OneNoteName, helpPaneNamespace: 'ONWAENDUSER' });
        this.supportedAppThemes.set(FileType.Forms, { theme: this.configThemeShadesToTheme(config.formsThemeShades), darkTheme: this.configThemeShadesToTheme(config.formsThemeShadesDark), headerText: 'Forms', helpPaneNamespace: 'MICROSOFTFORMS' });
        this.supportedAppThemes.set(FileType.Visio, { theme: this.configThemeShadesToTheme(config.visioThemeShades), darkTheme: this.configThemeShadesToTheme(config.visioThemeShadesDark), headerText: this.locService.VisioName, helpPaneNamespace: 'VIWAENDUSER' });
        this.supportedAppThemes.set(FileType.Stream, { theme: this.configThemeShadesToTheme(config.streamThemeShades), darkTheme: this.configThemeShadesToTheme(config.streamThemeShadesDark), headerText: this.locService.StreamName, helpPaneNamespace: 'STREAM' });

        // Fluid content pages want to use the homepage theme, so 'theme' is null.
        this.supportedAppThemes.set(FileType.Fluid, { theme: null, headerText: config.homePageTitle, helpPaneNamespace: 'FLUID365' });
        this.supportedAppThemes.set(FileType.FluidActivityBlueprint, { theme: null, headerText: config.homePageTitle, helpPaneNamespace: 'FLUID365' });

        this.userTheme = this.getInitialUserTheme();
    }

    // Our config sends theme shades without the '#' character. This adds it and the converts
    // to a BaseTheme
    private configThemeShadesToTheme(configShades: Array<string>): BaseTheme {
        return this.getAppBrandTheme(configShades.map(t => `#${t}`));
    }

    private getGreyShades() {
        if (this.capabilitiesService.capabilities.fluentRefresh) {
            if (this.showDarkMode()) {
                return ThemingService.grayscaleThemeShadesFluentDark;
            }
            return ThemingService.grayscaleThemeShadesFluent;
        }

        if (this.showDarkMode()) {
            return ThemingService.grayscaleThemeShadesDark;
        }
        return ThemingService.grayscaleThemeShades;
  }

    // For react shell, changes office.com app theme css.
    private changeAppTheme(themeShades: Array<string>, appPage?: FileType) {
        if (!themeShades || themeShades.length !== 9) {
            return;
        }

        const showDarkMode = this.showDarkMode();

        // Generates all css classes for the given theme shade, including focus, hover, and active states
        const themeShadeCss = (classPostfix, themeShade) => {
            let css = '';
            for (let i = 0; i < ThemingService.themeProperties.length; i++) {
                const prop = ThemingService.themeProperties[i];
                let propName = prop.propName;
                if (this.contextService.isRtl) {
                    propName = propName.replace(/(left|right)/g, match => match === 'left' ? 'right' : 'left');
                }
                css += `.${ThemeRootCssClass} .ms-${prop.className}-${classPostfix}{${propName}:${themeShade}}`; // main css class. Ex: .ms-bgc-tp { background-color: #tp }
                for (let j = 0; j < ThemingService.themeSelectorPostfixes.length; j++) {
                    // selector css for focus, hover, or active. Ex: .ms-bgc-tp-f:focus { background-color: #tp }
                    css += `.${ThemeRootCssClass} .ms-${prop.className}-${classPostfix}-${ThemingService.themeSelectorPostfixes[j]}{${propName}:${themeShade}}`;
                }
            }

            return css;
        };
        const grayShades = this.getGreyShades();
        const grayscaleThemeCss = grayShades.reduce((result, themeShade, index) => {
            const classPostfix = this.grayscaleThemeShadeClassPostfixes.get(index);
            const shadeCss = themeShadeCss(classPostfix, themeShade);
            return `${result}${shadeCss}`;
        }, '');

        const themeCss = themeShades.reduce((result, themeShade, index) => {
            const classPostfix = this.themeShadeClassPostfixes.get(index);
            const shadeCss = themeShadeCss(classPostfix, themeShade);
            return `${result}${shadeCss}`;
        }, grayscaleThemeCss);

        this.documentService.addOrUpdateStyleTag('ohp-app-theme', themeCss);

        this.changeFluentTheme(showDarkMode, appPage);
    }

    private brandThemeFromBase(baseTheme: BaseTheme) {
        return {
            themeDarker: baseTheme.Darker,
            themeDark: baseTheme.Dark,
            themeDarkAlt: baseTheme.DarkAlt,
            themePrimary: baseTheme.Primary,
            themeSecondary: baseTheme.Secondary,
            themeTertiary: baseTheme.Tertiary,
            themeLight: baseTheme.Light,
            themeLighter: baseTheme.Lighter,
            themeLighterAlt: baseTheme.LighterAlt
        };
    }

    private hasRequiredDarkShades(baseTheme: BaseTheme) {
        const darkTheme = baseTheme?.DarkTheme;
        return !!(darkTheme?.Dark && darkTheme?.DarkAlt && darkTheme?.Primary && darkTheme?.Tertiary && darkTheme?.Light && darkTheme?.Lighter);
    }

    private async changeFluentTheme(darkMode: boolean, appPage?: FileType) {
        const themeId = this.userTheme.themeId;

        // Used to ensure we only listen to shell shim callback for latest theme
        const themeKey = `${themeId}${darkMode}${appPage}`;
        this.lastFluentTheme = themeKey;

        const setFluentTheme = (lightTheme: BaseTheme, darkTheme?: BaseTheme) => {
            // TODO: Move mapping to office-start
            this.fluentThemeSubject.next({
                mode: darkMode ? Themes.dark : Themes.default,
                lightBrandColors: this.brandThemeFromBase(lightTheme),
                darkBrandColors: darkTheme && this.brandThemeFromBase(darkTheme)
            });
        };

        if (appPage && this.supportedAppThemes.has(appPage)) {
            const appTheme = this.supportedAppThemes.get(appPage);
            if (appTheme.theme) {
                setFluentTheme(appTheme.theme, appTheme.darkTheme);
                return;
            }
        }

        // NOTE: Tenant theme doesn't include dark mode palette, so use office
        if ((themeId === ThemingService.baseThemeId || themeId === ThemingService.staticDarkTheme) && (this.userTheme.reactShellTheme.PreferOfficeTheme || darkMode)) {
            const officeTheme = this.windowService.nativeWindow.commercialDefaultThemeFluent;
            setFluentTheme(officeTheme, officeTheme.DarkTheme);
            return;
        }

        // Consumer only has blue theme (and dark mode in HWA)
        if (this.clientStartupService.config.authType === AuthType.MSA) {
            setFluentTheme(ThemingService.msaFluentThemeLight, ThemingService.msaFluentThemeDark);
            return;
        }

        const shellShim = await this.shellShimService.getShellShim();

        shellShim.Theme.GetUserThemeJsonByID(themeId, theme => {
            if (themeKey !== this.lastFluentTheme) {
                return;
            }
            // In case a theme doesn't have required shades, fallback to office in dark mode
            if (!theme || darkMode && !this.hasRequiredDarkShades(theme)) {
                if (!theme) {
                    const params: InstrumentationParams = {
                        [InstrumentationParam.DebugInfo]: themeId
                    };
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.UnknownTheme, params);
                }
                const officeTheme = this.windowService.nativeWindow.commercialDefaultThemeFluent;
                setFluentTheme(officeTheme, officeTheme.DarkTheme);
                return;
            }

            setFluentTheme(theme, theme.DarkTheme);
        });
    }
}
